import type { LevelDefinition } from '../world/LevelDefinition';

const surfaces = {
  floor: { enabled: true, magnetic: true },
  ceiling: { enabled: true, magnetic: true },
  left: { enabled: true, magnetic: true },
  right: { enabled: true, magnetic: true },
  front: { enabled: true, magnetic: true },
  back: { enabled: true, magnetic: true },
};

const ceilingLights = (positions: Array<[number, number]>, y = 9.9) =>
  positions.map(([x, z]) => ({
    position: [x, y, z] as [number, number, number],
    size: [2.4, 0.08, 1.4] as [number, number, number],
  }));

/**
 * Reactor Gallery: a tall hub room around a central reactor block, with a side
 * gallery on each flank. The ring path circles the reactor.
 */
export const REACTOR_GALLERY: LevelDefinition = {
  id: 'reactor-gallery',
  name: 'Reactor Gallery',
  size: [40, 10, 20],
  surfaces,
  zones: [
    { id: 'gallery', origin: [0, 0, 0], size: [24, 10, 20] },
    { id: 'alcove-a', origin: [-16, 0, 0], size: [8, 8, 10] },
    { id: 'alcove-b', origin: [16, 0, 0], size: [8, 8, 10] },
  ],
  portals: [
    {
      id: 'gallery-a',
      a: 'gallery',
      b: 'alcove-a',
      axis: 'z',
      position: [-12, 0, 0],
      width: 3,
      height: 3.2,
    },
    {
      id: 'gallery-b',
      a: 'gallery',
      b: 'alcove-b',
      axis: 'z',
      position: [12, 0, 0],
      width: 3,
      height: 3.2,
    },
  ],
  spawn: { zone: 'gallery', surface: 'floor', position: [0, 0, 8], facing: 0 },
  targets: [
    { type: 'drone', zone: 'gallery', surface: 'floor', position: [0, 0, -8] },
    { type: 'drone', zone: 'gallery', surface: 'ceiling', position: [0, 10, 0] },
    { type: 'drone', zone: 'gallery', surface: 'left', position: [-12, 5, 6] },
    { type: 'drone', zone: 'gallery', surface: 'right', position: [12, 5, -6] },
    { type: 'drone', zone: 'alcove-a', surface: 'floor', position: [-16, 0, 0] },
    { type: 'drone', zone: 'alcove-b', surface: 'floor', position: [16, 0, 0] },
    { type: 'drone', zone: 'alcove-a', surface: 'ceiling', position: [-16, 8, 0] },
    { type: 'drone', zone: 'gallery', surface: 'back', position: [6, 5, 10] },
  ],
  obstacles: [
    // The reactor core: a large block in the middle of the ring.
    { type: 'box', kind: 'console', zone: 'gallery', position: [0, 2.5, 0], size: [6, 5, 6] },
    { type: 'box', kind: 'pillar', zone: 'gallery', position: [-8, 5, -8], size: [1.6, 10, 1.6] },
    { type: 'box', kind: 'pillar', zone: 'gallery', position: [8, 5, -8], size: [1.6, 10, 1.6] },
    { type: 'box', kind: 'pillar', zone: 'gallery', position: [-8, 5, 8], size: [1.6, 10, 1.6] },
    { type: 'box', kind: 'pillar', zone: 'gallery', position: [8, 5, 8], size: [1.6, 10, 1.6] },
    {
      type: 'box',
      kind: 'console',
      zone: 'alcove-a',
      position: [-17, 0.55, 3],
      size: [2, 1.1, 0.8],
    },
    {
      type: 'box',
      kind: 'console',
      zone: 'alcove-b',
      position: [17, 0.55, -3],
      size: [2, 1.1, 0.8],
    },
  ],
  style: {
    wallColor: 0x2a3038,
    floorColor: 0x1b2026,
    ceilingColor: 0xd4dee8,
    pathColor: 0xffb347,
    path: [
      { from: [-8, -6], to: [8, -6], width: 2.2 },
      { from: [8, -6], to: [8, 6], width: 2.2 },
      { from: [8, 6], to: [-8, 6], width: 2.2 },
      { from: [-8, 6], to: [-8, -6], width: 2.2 },
      { from: [-12, 0], to: [-20, 0], width: 2 },
      { from: [12, 0], to: [20, 0], width: 2 },
    ],
    ceilingLights: [
      ...ceilingLights([
        [-8, -6],
        [8, -6],
        [-8, 6],
        [8, 6],
        [0, 0],
      ]),
      // The side galleries are only 8 m tall, so their panels sit lower.
      ...ceilingLights(
        [
          [-16, 0],
          [16, 0],
        ],
        7.9,
      ),
    ],
  },
  seed: 10241,
};
